import { generateFieldName, type ViewConfigField } from '@salescore/core'
import { Form, InputNumber, Select, Space } from 'antd'
import { Array } from 'effect'
import { isNullish } from 'effect/Predicate'
import type { TFunction } from 'i18next'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** N日前 */
const DAYS_BEFORE = 'daysBefore'
/** N日後 */
const DAYS_AFTER = 'daysAfter'

/** 選択肢が持つ値。数値はレコードを作成した日からの日数で、そのまま保存する */
type DayOffsetChoice = number | typeof DAYS_BEFORE | typeof DAYS_AFTER

/** 選択肢に日数を持たせている範囲。この外の日数は N 日前 / N 日後として入力欄で受け取る */
const LABELED_DAY_OFFSET_LIMIT = 7

/** 選択欄に並べる日数の選択肢を組み立てる */
const buildDayOffsetOptions = (t: TFunction): Array<{ value: DayOffsetChoice; label: string }> => [
  { value: -1, label: t('昨日') },
  { value: -2, label: t('一昨日') },
  ...Array.range(3, LABELED_DAY_OFFSET_LIMIT).map((x) => ({ value: -x, label: t('{{x}}日前', { x }) })),
  { value: DAYS_BEFORE, label: t('N日前') },
  { value: 0, label: t('今日') },
  { value: 1, label: t('明日') },
  { value: 2, label: t('明後日') },
  ...Array.range(3, LABELED_DAY_OFFSET_LIMIT).map((x) => ({ value: x, label: t('{{x}}日後', { x }) })),
  { value: DAYS_AFTER, label: t('N日後') },
]

/**
 * 保存されている日数から、選択欄に表示する選択肢を求める
 * @param dayOffset レコードを作成した日からの日数
 * @returns 日数を持つ選択肢。選択肢の範囲外なら N 日前 / N 日後
 */
const toDayOffsetChoice = (dayOffset: number | undefined): DayOffsetChoice | undefined => {
  if (isNullish(dayOffset)) {
    return undefined
  }
  if (Math.abs(dayOffset) <= LABELED_DAY_OFFSET_LIMIT) {
    return dayOffset
  }
  return dayOffset < 0 ? DAYS_BEFORE : DAYS_AFTER
}

/**
 * 選択欄の選択肢と日数の入力欄から、保存する日数を求める。
 * N 日前と N 日後を切り替えたときは、入力欄に残っている日数をそのまま使う
 * @param choice 選択欄で選んでいる選択肢
 * @param days N 日前 / N 日後の入力欄の値
 * @returns レコードを作成した日からの日数。選択肢が未選択、または日数が未入力なら undefined
 */
const toDayOffset = (choice: DayOffsetChoice | undefined, days: unknown): number | undefined => {
  if (typeof choice === 'number') {
    return choice
  }
  // 選択を外した直後は入力欄がまだ Form に残っていて日数を読めるため、選択肢が無いことを先に確かめる
  if (isNullish(choice) || typeof days !== 'number') {
    return undefined
  }
  return choice === DAYS_BEFORE ? -days : days
}

/**
 * date / datetime 型の項目に、レコードを作成した日からの日数をデフォルト値として設定する。
 * 選択肢は KPI の絞り込みで使っている相対日時に合わせる。
 * 日数の入力欄を antd の Form に登録して読み取り・検証するため、デフォルト値設定の Form の中で使う
 * @param field 設定対象の項目。設定済みの日数を初期表示に使う
 * @param setDefaultValue 保存する日数を渡す。解除したときは undefined を渡す
 */
export const FieldDefaultValueDateInput = ({
  field,
  setDefaultValue,
}: {
  field: ViewConfigField
  setDefaultValue: (defaultValue: number | undefined) => void
}): ReactElement => {
  const defaultValue = field.override?.queryField?.meta?.defaultValue
  const savedDayOffset = typeof defaultValue === 'number' ? defaultValue : undefined
  // 入力中は選択肢を保存された日数から求め直さない。N 日前のまま日数だけを変えられるようにする
  const [choice, setChoice] = useState<DayOffsetChoice | undefined>(() => toDayOffsetChoice(savedDayOffset))
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const daysFieldName = `${generateFieldName(field.property)}_days`
  const dayOffsetOptions = buildDayOffsetOptions(t)
  const isDaysInputVisible = choice === DAYS_BEFORE || choice === DAYS_AFTER

  return (
    <Space.Compact className="w-full">
      <Select
        className={isDaysInputVisible ? 'w-3/5' : 'w-full'}
        allowClear
        placeholder={t('デフォルト値なし')}
        options={dayOffsetOptions}
        value={choice}
        onChange={(value: DayOffsetChoice | undefined) => {
          setChoice(value)
          // 選択欄の変更では日数が渡らないため、入力欄に残っている日数を Form から読む
          const days: unknown = form.getFieldValue(daysFieldName)
          const dayOffset = toDayOffset(value, days)
          setDefaultValue(dayOffset)
        }}
      />
      {isDaysInputVisible && (
        // 日数を入れないまま「適用」を押せないようにする。項目ごとに一意の名前で Form に登録する
        <Form.Item
          noStyle
          preserve={false}
          name={daysFieldName}
          initialValue={isNullish(savedDayOffset) ? undefined : Math.abs(savedDayOffset)}
          rules={[{ required: true, message: t('日数を入力してください') }]}
        >
          <InputNumber
            className="w-2/5"
            placeholder={t('Nの値')}
            min={1}
            precision={0}
            onChange={(days) => {
              const dayOffset = toDayOffset(choice, days)
              setDefaultValue(dayOffset)
            }}
          />
        </Form.Item>
      )}
    </Space.Compact>
  )
}
